import React, { useCallback, ReactElement, ForwardedRef } from 'react';
import TextField from '@mui/material/TextField';
import FormatService from '../../services/format-service';
import useStylesFieldNumber from './field-number.styles';
import { DATA_REF_NAME } from '../../constants/system/data-attributes';

const FieldNumber: ReactMemoComponent = React.memo(React.forwardRef((
  props: ReactComponentProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement => {
  const VARIANT_OUTLINED: TextFieldVariant = 'outlined';
  const AUTOCOMPLETE_ON: string = 'on';
  const AUTOCOMPLETE_OFF: string = 'off';
  const MAX_LENGTH: number = 20;

  const {
    isPlain,
    isShort,
    isExtraShort,
    isFloat,
    isPercent,
    isOutlined,
    isBordered,
    isDisabled,
    withNoLimit,
    withNoCommas,
    withUnderline,
    withAutocomplete,
    withNoAutocomplete,
    withNegativeValues,
    className,
    label,
    value,
    placeholder,
    maxLength,
    inputRefs,
    inputRefIndex,
    onChange,
    onKeyDown,
    input,
    meta,
    ...custom
  } = props;

  const {
    touched: isTouched,
    invalid: isInvalid,
    error: formError,
    submitError: formSubmitError,
    modifiedSinceLastSubmit,
  } = (meta || {}) as FormFieldMetaProps;

  const { classes } = useStylesFieldNumber();

  const borderedClassName: string | undefined = isBordered ? classes.fieldNumberBordered : undefined;
  const outlinedClassName: string | undefined = isOutlined ? classes.fieldNumberOutlined : undefined;
  const noUnderlineClassName: string | undefined = !withUnderline ? classes.fieldNumberNoUnderline : undefined;
  const shortClassName: string | undefined = isShort ? classes.fieldNumberShort : undefined;
  const extraShortClassName: string | undefined = isExtraShort ? classes.fieldNumberExtraShort : undefined;

  const withErrors: boolean = ((isTouched && isInvalid) || !!formError || !!formSubmitError)
    && !modifiedSinceLastSubmit;

  const limit: number | undefined = isPercent && !withNoLimit ? FormatService.PERCENT_NUMBER_LIMIT : undefined;
  const autoCompleteOn: string | undefined = withAutocomplete ? AUTOCOMPLETE_ON : undefined;
  const autoCompleteOff: string | undefined = withNoAutocomplete ? AUTOCOMPLETE_OFF : undefined;
  const autoCompleteValue: string | undefined = autoCompleteOn || autoCompleteOff;

  const keyDown: Function = useCallback((event: React.KeyboardEvent): void => {
    if (event.ctrlKey || event.shiftKey || FormatService.isSystemKey(event.key, isFloat, withNegativeValues)) {
      return;
    }

    const isNumberKey: boolean = FormatService.isNumberKey(event.key);
    if (!isNumberKey) {
      return;
    }

    const resultValue: string | null = FormatService.processNumberInput(
      event.target as HTMLInputElement, isFloat, withNoCommas, withNegativeValues, limit,
    );

    if (onKeyDown) {
      onKeyDown(resultValue);
    }
  }, [isFloat, withNoCommas, withNegativeValues, limit]);

  const change: Function = useCallback((event: React.ChangeEvent): void => {
    const resultValue: string | null = FormatService.processNumberInput(
      event.target as HTMLInputElement, isFloat, withNoCommas, withNegativeValues, limit,
    );

    if (onChange) {
      onChange(resultValue);
    }
  }, [isFloat, withNoCommas, withNegativeValues, limit]);

  return (
    <TextField
      disabled={isDisabled}
      ref={ref}
      label={label}
      placeholder={placeholder || label}
      error={withErrors}
      helperText={withErrors && (formError || formSubmitError)}
      variant={isOutlined ? VARIANT_OUTLINED : undefined}
      className={FormatService.formatClassNames(
        classes.fieldNumber, borderedClassName, outlinedClassName, noUnderlineClassName, shortClassName,
        extraShortClassName, className,
      )}
      inputProps={{
        maxLength: maxLength || MAX_LENGTH,
        [DATA_REF_NAME]: inputRefIndex,
      }}
      inputRef={(
        inputRefs ? (ref: HTMLInputElement | null): void => {
          inputRefs.current[inputRefIndex] = ref;
        } : null
      )}
      onKeyDown={keyDown}
      onChange={change}
      {
        ...(autoCompleteValue ? {
          autoComplete: autoCompleteValue,
        } : {})
      }
      {
        ...(!isPlain ? {
          ...input,
          value: FormatService.formatNumber(input.value, isFloat, limit, !withNegativeValues, withNoCommas),
        } : {})
      }
      {...custom}
    />
  );
}));

export default FieldNumber;
